import React, {useEffect} from "react";
import {
    IonButton,
    IonCard, IonCardContent,
    IonCardHeader, IonCardTitle,
    IonContent, IonIcon, IonItem, IonLabel,
    IonLoading,
    IonPage, IonPopover,
    IonSelect,
    IonSelectOption, IonToast, useIonAlert, useIonModal
} from "@ionic/react";
import {
    addOutline,
    addSharp,
    attachOutline,
    attachSharp,
    cloudUploadOutline,
    cloudUploadSharp,
    copyOutline,
    copySharp,
    documentOutline,
    documentSharp,
    eyeOffOutline,
    eyeOffSharp,
    eyeOutline,
    eyeSharp, informationOutline, informationSharp,
    paperPlaneOutline,
    paperPlaneSharp,
    personOutline,
    personSharp,
    trashOutline,
    trashSharp
} from "ionicons/icons";
import './AcaViewOpportunity.css';
import ModalImage from "./modals/ModalImage";
import ModalCloseOpportunity from "./modals/ModalCloseOpportunity";
import {
    ACA_CREATE_NOTE,
    ACA_GET_CONTACT, ACA_GET_CONTACT_UNMASKED, ACA_GET_HISTORY, ACA_GET_IMAGE_URL,
    ACA_GET_OPPORTUNITY, ACA_GET_PIPELINE_STAGES, ACA_GET_PROVIDER_STATES, ACA_ISSUE_IMAGE_REPLACEMENT,
    ACA_UPDATE_CONTACT, ACA_UPDATE_OPPORTUNITY, ACA_UPLOAD_IMAGE,
} from "../../schemas/aca";
import {useMutation} from "@apollo/client";
import {
    AcaGetOpportunityResponseOpportunity,
    ContactNote,
    ContactType,
    AcaGetPipelineStagesResponseStage,
    Attachment
} from "./types";
import {useHistory, useRouteMatch} from "react-router";
import {formatDate, formatDatetime, formatPhone, formatStatus} from "../../utils";
import useAuth from "../../hooks/useAuth";
import {AcaHeader} from "./AcaHeader";
import {OverlayEventDetail} from "@ionic/react/dist/types/components/react-component-lib/interfaces";
import ModalNote from "./modals/ModalNote";
import {AcaGetHistoryResponseHistory } from "@backend/types/aca";
import AcaUploadFile, {AcaSingleUploadI} from "./components/AcaUploadFile";
import ModalImageReplacementRequest from "./modals/ModalImageReplacementRequest";
import MissingItem from "../../components/MissingItem";
import ModalMoreDetails from "./modals/ModalMoreDetails";
import moment from "moment";
import ModalPopup from "./modals/ModalPopup";


const AcaViewOpportunity: React.FC<any> = () => {
    const {claims} = useAuth();
    const history = useHistory();
    const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);
    const [showPopover, setShowPopover] = React.useState<boolean>(false);
    const [showNotes, setShowNotes] = React.useState<boolean>(true);
    const [opportunity, setOpportunity] = React.useState<AcaGetOpportunityResponseOpportunity | undefined>(undefined);
    const [contact, setContact] = React.useState<ContactType | undefined>(undefined);
    const [contactNotes, setContactNotes] = React.useState<ContactNote[] | undefined>(undefined);
    const [historyEntries, setHistoryEntries] = React.useState<AcaGetHistoryResponseHistory[] | undefined>(undefined);
    const [availableStages, setAvailableStages] = React.useState<AcaGetPipelineStagesResponseStage[] | undefined>(undefined);
    const [newStage, setNewStage] = React.useState<number | undefined>(undefined);
    const [assignedTo, setAssignedTo] = React.useState<string | undefined>(undefined);
    const [availableUsers, setAvailableUsers] = React.useState<{userRef: string, userName: string}[] | undefined>(undefined);
    const [providerStates, setProviderStates] = React.useState<{name: string, state: string}[] | undefined>(undefined);
    const [imageUrl, setImageUrl] = React.useState<string | undefined>(undefined);
    const [masked, setMasked] = React.useState<boolean>(true);
    const [replacementPdfType, setReplacementPdfType] = React.useState<'AOR_PDF' | 'LEAD_PDF' | undefined>(undefined);
    const [docTitle, setDocTitle] = React.useState<string>('');
    const [docPossibleDocument, setDocPossibleDocument] = React.useState<{description: string, bullets: string[]} | null | undefined>(null);

    const [tier1, setTier1] = React.useState<boolean>(false);
    const [tier2, setTier2] = React.useState<boolean>(false);
    const [manager, setManager] = React.useState<boolean>(false);

    const [presentImage, dismissImage] = useIonModal(ModalImage, {
        imageUrl: imageUrl,
        dismiss: (data: string, role: string) => dismissImage(data, role)
    });
    const [presentClose, dismissClose] = useIonModal(ModalCloseOpportunity, {
        providerStates: providerStates,
        isTier2: tier2 && !manager,
        opportunity: opportunity,
        contact: contact,
        dismiss: (data: string, role: string) => dismissClose(data, role)
    });
    const [presentImageReplacement, dismissImageReplacement] = useIonModal(ModalImageReplacementRequest, {
        contact: contact,
        replacementType: replacementPdfType,
        dismiss: (data: string, role: string) => dismissImageReplacement(data, role)
    });
    const [presentAddNote, dismissAddNote] = useIonModal(ModalNote, {dismiss: (data: string, role: string) => dismissAddNote(data, role)});
    const [presentDocDetail, dismissDocDetail] = useIonModal(ModalPopup, {title:docTitle, body: docPossibleDocument, dismiss: (data: string, role: string) => dismissDocDetail(data, role)});
    const [presentMoreDetails, dismissMoreDetails] = useIonModal(ModalMoreDetails, {
        dismiss: (data: string, role: string) => dismissMoreDetails(data, role),
        pipelineStage: availableStages?.find((stage) => stage.id===newStage)
    });

    const [acaGetOpportunityMutation] = useMutation(ACA_GET_OPPORTUNITY);
    const [acaGetContactMutation] = useMutation(ACA_GET_CONTACT);
    const [acaGetContactUnmaskedMutation] = useMutation(ACA_GET_CONTACT_UNMASKED);
    const [updateContactMutation] = useMutation(ACA_UPDATE_CONTACT);
    const [updateOpportunityMutation] = useMutation(ACA_UPDATE_OPPORTUNITY);
    const [addNoteMutation] = useMutation(ACA_CREATE_NOTE);
    const [getHistoryMutation] = useMutation(ACA_GET_HISTORY);
    const [getStagesMutation] = useMutation(ACA_GET_PIPELINE_STAGES);
    const [getProviderStatesMutation] = useMutation(ACA_GET_PROVIDER_STATES);
    const [getImageUrlMutation] = useMutation(ACA_GET_IMAGE_URL);
    const [uploadImageMutation] = useMutation(ACA_UPLOAD_IMAGE);
    const [issueImageReplacementMutation] = useMutation(ACA_ISSUE_IMAGE_REPLACEMENT);
    const [presentAlert, dismissAlert] = useIonAlert();

    const match:any = useRouteMatch();


    // replacing images
    const [changingLeadPdf, setChangingLeadPdf] = React.useState<boolean>(false);
    const [leadAttachments, setLeadAttachments] = React.useState<Attachment[]>([]);
    const [changingAorPdf, setChangingAorPdf] = React.useState<boolean>(false);
    const [aorAttachments, setAorAttachments] = React.useState<Attachment[]>([]);

    const addLeadAttachment = async ({ filename, url }: AcaSingleUploadI) => {
        const fileParts = filename.split('.');
        const fileType = fileParts[fileParts.length-1];
        if (fileType.toUpperCase() !== 'PDF') {
            return alert('Only able to upload PDF files');
        }
        setIsLoading(true);

        let newFileType : 'PDF' = 'PDF';

        if (contact && contact.contactId && opportunity && opportunity.opportunityId) {
            setLeadAttachments([{filename, url, label: ""}, ...leadAttachments])
            const uploadResult = await uploadImageMutation({
                variables: {
                    imageUrl: url,
                    imageName: filename,
                    imageFileType: newFileType
                }
            });
            const updateOpportunityResult = await updateOpportunityMutation({
                variables: {
                    data: {
                        opportunityId: opportunity.opportunityId,
                        leadSignedPdfId: Number(uploadResult.data.acaUploadImage.imageId)
                    }
                }
            });
            setChangingLeadPdf(false);
            await loadData(opportunity.opportunityId);
        }
    }
    const addAorAttachment = async ({ filename, url }: AcaSingleUploadI) => {
        if (contact && contact.contactId && opportunity && opportunity.opportunityId) {
            const fileParts = filename.split('.');
            const fileType = fileParts[fileParts.length-1];
            if (fileType.toUpperCase() !== 'PDF') {
                return alert('Only able to upload PDF files');
            }

            setIsLoading(true);

            let newFileType : 'PDF' = 'PDF';
            setAorAttachments([{filename, url, label: ""}, ...aorAttachments])
            const uploadResult = await uploadImageMutation({
                variables: {
                    imageUrl: url,
                    imageName: filename,
                    imageFileType: newFileType
                }
            });
            const updateOpportunityResult = await updateOpportunityMutation({
                variables: {
                    data: {
                        opportunityId: opportunity.opportunityId,
                        aorSignedPdfId: Number(uploadResult.data.acaUploadImage.imageId)
                    }
                }
            });
            setChangingAorPdf(false);

            await loadData(opportunity.opportunityId);
        }
    }
    ////

    useEffect(() => {
        // check for roles
        if (!claims.rules.includes('AcaView')) {
            history.push('/');
            return;
        }
        if (claims.rules.includes('AcaEditTier2')) {
            setTier2(true);
        }
        if (claims.rules.includes('AcaEditTier1')) {
            setTier1(true);
        }
        if (claims.rules.includes('AcaManager')) {
            setManager(true);
        }
        if (match.params.id && !isNaN(match.params.id)) {
            init();
        }

    }, []);
    useEffect(() => {
        pullStages();
    }, [opportunity]);

    const init = async () => {
        await loadData(Number(match.params.id));
    }


    const loadData = async (id: number, ismasked?: boolean) => {
        if (ismasked === undefined || ismasked === null) {
            ismasked = masked;
        }
        setIsLoading(true);
        try {
            let lookupResult = await acaGetOpportunityMutation({variables: {opportunityId: id}});
            if (lookupResult.data?.acaGetOpportunity.success) {
                const opp = lookupResult.data?.acaGetOpportunity.opportunity;
                setOpportunity(opp);

                let contactResult;
                if (!ismasked && (tier2 || manager)) {
                    contactResult = await acaGetContactUnmaskedMutation({variables: {contactId: opp.contactId}});
                    if (contactResult.data?.acaGetContactUnmasked.success) {
                        setContact(contactResult.data.acaGetContactUnmasked.contact);
                        setContactNotes(contactResult.data.acaGetContactUnmasked.notes);
                    }
                } else {
                    contactResult = await acaGetContactMutation({variables: {contactId: opp.contactId}});
                    if (contactResult.data?.acaGetContact.success) {
                        setContact(contactResult.data.acaGetContact.contact);
                        setContactNotes(contactResult.data.acaGetContact.notes);
                    }
                }
                setNewStage(opp.stageId);
            }


        } catch (e: any) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    }
    const unmaskData = async () => {
        if (opportunity && opportunity.opportunityId) {
            setMasked(false);
            await loadData(opportunity.opportunityId, false);
        }
    };
    const maskData = async () => {
        if (opportunity && opportunity.opportunityId) {
            setMasked(true);
            await loadData(opportunity.opportunityId, true);
        }
    };

    const toggleNotes = async (shouldShowNotes: boolean) => {
        setShowNotes(shouldShowNotes);
        if (!shouldShowNotes) {
            pullHistory();
        }

    };

    const loadPdf = async (imageId: number | null)=> {
        if (imageId) {
            const imageLookup = await getImageUrlMutation({variables: {imageId}});
            if (!imageLookup.data.acaGetImageUrl.success) {
                return alert('Unable to lookup image!');
            }
            window.open(imageLookup.data.acaGetImageUrl.url);
        }
    }

    const expandImage = async (imageId: number | null) => {
        if (imageId) {
            const imageLookup = await getImageUrlMutation({variables: {imageId}});
            if (!imageLookup.data.acaGetImageUrl.success) {
                return alert('Unable to lookup image!');
            }
            setImageUrl(imageLookup.data.acaGetImageUrl.url);
            await presentImage();
        }

    };

    const prepLinks = async (history: AcaGetHistoryResponseHistory[] | undefined)=> {

        history?.forEach((history) => {
            if (history.changeText.match(/Photo 1 changed from \[(\d+)] to \[(\d+)]/)) {
                const found = history.changeText.match(/Photo 1 changed from \[(\d+)] to \[(\d+)]/);
                if (found && found.length > 2) {
                    history.changeText = 'Photo 1 changed from [<a href="/aca/view-image/' + found[1] + '" target="_blank">' + found[1] + '</a>]'
                                        + ' to [<a href="/aca/view-image/'+found[2]+'" target="_blank">'+found[2]+'</a>]';
                }
            } else if (history.changeText.match(/Photo 2 changed from \[(\d+)] to \[(\d+)]/)) {
                const found = history.changeText.match(/Photo 2 changed from \[(\d+)] to \[(\d+)]/);
                if (found && found.length > 2) {
                    history.changeText = 'Photo 2 changed from [<a href="/aca/view-image/' + found[1] + '" target="_blank">' + found[1] + '</a>]'
                        + ' to [<a href="/aca/view-image/'+found[2]+'" target="_blank">'+found[2]+'</a>]';
                }
            } else if (history.changeText.match(/Lead consent PDF changed from \[(\d+)] to \[(\d+)]/) && tier2) {
                const found = history.changeText.match(/Lead consent PDF changed from \[(\d+)] to \[(\d+)]/);
                if (found && found.length > 2) {
                    history.changeText = 'Lead consent PDF changed from [<a href="/aca/view-image/' + found[1] + '" target="_blank">' + found[1] + '</a>]'
                        + ' to [<a href="/aca/view-image/'+found[2]+'" target="_blank">'+found[2]+'</a>]';
                }
            } else if (history.changeText.match(/AOR consent PDF changed from \[(\d+)] to \[(\d+)]/) && tier2) {
                const found = history.changeText.match(/AOR consent PDF changed from \[(\d+)] to \[(\d+)]/);
                if (found && found.length > 2) {
                    history.changeText = 'AOR consent PDF changed from [<a href="/aca/view-image/' + found[1] + '" target="_blank">' + found[1] + '</a>]'
                        + ' to [<a href="/aca/view-image/'+found[2]+'" target="_blank">'+found[2]+'</a>]';
                }
            }
        });
        return history;
    }

    const pullHistory = async () => {
        if (opportunity && opportunity.contactId) {
            setIsLoading(true);
            const contactId = Number(opportunity.contactId);
            try {
                const historyResponse = await getHistoryMutation({variables: {contactId: contactId}});
                if (!historyResponse.data.acaGetHistory.success) {
                    alert(historyResponse.data.acaGetHistory.error);
                } else {
                    setHistoryEntries(await prepLinks(historyResponse.data.acaGetHistory.history));
                }
            } catch (e: any) {
                console.log(e);
                alert(e.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const pullStages = async () => {
        const stagesResponse = await getStagesMutation({variables: {pipelineId: 1}});
        if (!stagesResponse.data.acaGetPipelineStages.success) {
            alert(stagesResponse.data.acaGetPipelineStages.error)
        } else {
            if (opportunity && opportunity?.status === 'OPEN') { // is open
                setAvailableStages(stagesResponse.data.acaGetPipelineStages.stages.filter((stage: AcaGetPipelineStagesResponseStage) => !stage.enrolled && !stage.failed && !stage.closedOption));
            } else if (opportunity && opportunity?.status === 'CLOSED_WON') { // closed won
                setAvailableStages(stagesResponse.data.acaGetPipelineStages.stages.filter((stage: AcaGetPipelineStagesResponseStage) => stage.enrolled || stage.closedOption || (manager && stage.id === 1)));
            } else { // closed lost
                setAvailableStages([]);
            }
        }
    };

    const updateStage = async (stageId: number | undefined | null) => {
        if (!stageId || isNaN(stageId) || typeof stageId !== 'number') {
            return;
        }
        if (newStage !== stageId && availableStages) {
            const stage = availableStages.find((item) => {
                return item.id === stageId;
            });
            setNewStage(stageId);

            if (stageId && opportunity && stageId !== opportunity?.stageId) {
                if (stage?.closedOption) {
                    presentMoreDetails({
                        onWillDismiss: async (ev: CustomEvent<OverlayEventDetail>) => {
                            setIsLoading(true);
                            if (ev.detail.role === 'confirm') {
                                let updateOppResponse = await updateOpportunityMutation({
                                    variables: {
                                        data: {
                                            opportunityId: opportunity.opportunityId,
                                            stageId: stageId,
                                            verificationDate: ev.detail.data.verificationDate,
                                            verificationDataRequested: ev.detail.data.verificationDataRequested
                                        }
                                    }
                                });
                                if (!updateOppResponse.data.acaUpdateOpportunity.success) {
                                    setIsLoading(false);
                                    return alert(updateOppResponse.data.acaUpdateOpportunity.error);
                                }
                                if (opportunity.opportunityId) {
                                    await loadData(opportunity.opportunityId);
                                }
                            } else {
                                setNewStage(undefined);
                            }
                            setIsLoading(false);
                        }
                    });
                } else {
                    let updateOppResponse = await updateOpportunityMutation({
                        variables: {
                            data: {
                                opportunityId: opportunity.opportunityId,
                                stageId: stageId
                            }
                        }
                    });
                    if (!updateOppResponse.data.acaUpdateOpportunity.success) {
                        return alert(updateOppResponse.data.acaUpdateOpportunity.error);
                    }
                    if (opportunity.opportunityId) {
                        await loadData(opportunity.opportunityId);
                    }
                }
            }
        }
    };

    const updateAssignedTo = async() => {
        if (opportunity && opportunity.opportunityId) {
            let updateOppResponse = await updateOpportunityMutation({variables: {data: {opportunityId: opportunity.opportunityId, assignToMe: true}}});
            if (!updateOppResponse.data.acaUpdateOpportunity.success) {
                return alert(updateOppResponse.data.acaUpdateOpportunity.error);
            }

            loadData(opportunity.opportunityId);
        }
    };
    const removeAssignedUser = async () => {
        if (opportunity) {
            let updateOppResponse = await updateOpportunityMutation({
                variables: {
                    data: {
                        opportunityId: opportunity.opportunityId,
                        assignedUserRef: null,
                        assignedUserName: null
                    }
                }
            });
            if (!updateOppResponse.data.acaUpdateOpportunity.success) {
                return alert(updateOppResponse.data.acaUpdateOpportunity.error);
            }
            if (opportunity.opportunityId) {
                loadData(opportunity.opportunityId);
            }
        }
    };

    const popupAddNote = async () => {
        if (opportunity) {
            presentAddNote({
                onWillDismiss: async (ev: CustomEvent<OverlayEventDetail>) => {
                    setIsLoading(true);
                    if (ev.detail.role === 'confirm') {
                        const popupData = ev.detail.data;
                        let addNoteResponse = await addNoteMutation({variables: {data: {contactId: opportunity.contactId, noteText: popupData}}});
                        if (!addNoteResponse.data.acaCreateNote.success) {
                            return alert(addNoteResponse.data.acaCreateNote.error);
                        } else {
                            let contactResult = await acaGetContactMutation({variables: {contactId: opportunity.contactId}});
                            if (contactResult.data?.acaGetContact.success) {
                                setContact(contactResult.data.acaGetContact.contact);
                                setContactNotes(contactResult.data.acaGetContact.notes);
                            }
                        }
                    }
                    setIsLoading(false);
                }
            });
        }
    };

    const handleCloseOpp = async () => {
        if (opportunity) {
            // pull current providers by state
            const providerStates = await getProviderStatesMutation();
            if (!providerStates.data.acaGetProviderStates.success) {
                return alert('Issue loading providers');
            }
            setProviderStates(providerStates.data.acaGetProviderStates.providerStates);
             presentClose({
                 onWillDismiss: async (ev: CustomEvent<OverlayEventDetail>) => {
                     if (ev.detail.role === 'confirm') {
                         const popupData = ev.detail.data;
                         // available contact statuses enum('CURRENT', 'POTENTIAL', 'PREVIOUS', 'NO_CONTACT', 'DECEASED', 'NOT_ELIGIBLE')
                         if (popupData.enrolled) {
                             // then require carrier provider, carrier state, carrier member count
                             // update status to CLOSED_WON
                             // change stageId to Enrolled (4)
                             // update contact status to CURRENT
                             // require that signed documents are good

                             if (!popupData.carrierProvider) {
                                 return alert('Please select a carrier provider');
                             }
                             if (!popupData.carrierMemberCount) {
                                 return alert('Please select how many members are enrolled');
                             }
                             if (!popupData.carrierState) {
                                 return alert('Please select the state that the member is enrolled in');
                             }
                             try {
                                 let updateOppResponse = await updateOpportunityMutation({variables: {data: {opportunityId: opportunity.opportunityId, status: 'CLOSED_WON', stageId: 4, carrierProvider: popupData.carrierProvider, carrierState: popupData.carrierState, carrierMemberCount: Number(popupData.carrierMemberCount)}}});
                                 if (!updateOppResponse.data.acaUpdateOpportunity.success) {
                                     return alert(updateOppResponse.data.acaUpdateOpportunity.error);
                                 }
                                 let updateContactResponse = await updateContactMutation({variables: {data: {contactId: opportunity.contactId, status: 'CURRENT'}}});
                                 if (!updateContactResponse.data.acaUpdateContact.success) {
                                     console.error(updateContactResponse.data.acaUpdateContact.error); // this is ok, sometimes this does not change and could throw error
                                 }
                            } catch (e: any) {
                                 console.log(e);
                                 return alert(e.message);
                             }
                         } else {
                             // then require a failed reason code, and a note
                             // update status to CLOSED_LOST
                             // change stageId to Failed (5)
                             // update contact status to ????? (see below options)
                             // Competition - NOT_ELIGIBLE
                             // Pricing/Cost - NOT_ELIGIBLE
                             // Incomplete Lead Data - NOT_ELIGIBLE
                             // No Response - NOT_ELIGIBLE
                             // Income - NOT_ELIGIBLE
                             // Medicaid - NOT_ELIGIBLE
                             // Medicare - NOT_ELIGIBLE
                             // Employer Benefits - NOT_ELIGIBLE
                             if (!popupData.failedReasonCode) {
                                 return alert('Please select a reason');
                             }
                             if (!popupData.failedNote) {
                                 return alert('Please leave a note as to why the opportunity is being closed');
                             }
                             try {
                                 let updateOppResponse = await updateOpportunityMutation({variables: {data: {opportunityId: opportunity.opportunityId, status: 'CLOSED_LOST', stageId: 5, failedReasonCode: popupData.failedReasonCode, failedNote: popupData.failedNote}}});
                                 if (!updateOppResponse.data.acaUpdateOpportunity.success) {
                                     return alert(updateOppResponse.data.acaUpdateOpportunity.error);
                                 }
                                 let updateContactResponse = await updateContactMutation({variables: {data: {contactId: opportunity.contactId, status: 'NOT_ELIGIBLE'}}});
                                 if (!updateContactResponse.data.acaUpdateContact.success) {
                                     // not always updating contact data or having changes, errors can occur here, it's fine though.
                                     console.error(updateContactResponse.data.acaUpdateContact.error);
                                 }

                                 // add note
                                 let addNoteResponse = await addNoteMutation({variables: {data: {contactId: opportunity.contactId, noteText: 'Closing reason: ' + popupData.failedNote}}});
                                 if (!addNoteResponse.data.acaCreateNote.success) {
                                     return alert(addNoteResponse.data.acaCreateNote.error);
                                 }
                             } catch (e: any) {
                                 console.log(e);
                                 return alert(e.message);
                             }
                         }

                         if (opportunity.opportunityId) {
                             loadData(opportunity.opportunityId);
                         }
                     } else if (ev.detail.role === 'cancel') {
                         // cancel do nothing

                     }
                 },
             });
        }
    };

    const promptPdfReplace = async (docType: 'LEAD_PDF' | 'AOR_PDF') => {
        if (contact && opportunity) {
            if (docType === 'LEAD_PDF') {
                setReplacementPdfType('LEAD_PDF');
                setChangingLeadPdf(false); // reset changing option in case it was clicked before this
            } else {
                setReplacementPdfType('AOR_PDF');
                setChangingAorPdf(false);  // reset changing option in case it was clicked before this
                // do a little extra check for required data for AOR consent
                if (!contact.dob) {
                    return await presentAlert("Date of Birth is required before collecting Agent Of Record Consent");
                }
                if (!contact.ssn) {
                    return await presentAlert("Social Security Number is required before collecting Agent Of Record Consent");
                }
            }
            presentImageReplacement({
                onWillDismiss: async (ev: CustomEvent<OverlayEventDetail>) => {
                    if (ev.detail.role === 'confirm') {
                        setIsLoading(true);
                        let contactMethod: 'EMAIL' | 'SMS' = 'EMAIL';
                        if (ev.detail.data === 'SMS') {
                            contactMethod = 'SMS';
                        }

                        let result = await issueImageReplacementMutation({
                            variables: {
                                data: {
                                    type: docType,
                                    deliveryMethod: contactMethod,
                                    contactId: contact.contactId,
                                    contactOpportunityId: opportunity.opportunityId
                                }
                            }
                        });
                        setIsLoading(false);
                        if (!result.data.acaIssueImageReplacement.success) {
                            return alert(result.data.acaIssueImageReplacement.error);
                        }

                        if (contactMethod === 'SMS') {
                            return await presentAlert('Request has been sent to ' + formatPhone(contact.phone));
                        } else {
                            return await presentAlert('Request has been sent to ' + contact.email);
                        }
                    }
                }
            });
        }
    };
    const copyToClipboard = async (contactId: number | null | undefined) => {
        if (contactId) {
            await navigator.clipboard.writeText(contactId.toString());
            setShowPopover(true);
        }
    };
    const popupDocDocumentType = async (title: string, body: string)=> {
        setDocTitle(title);
        try {
            const parsedBody = JSON.parse(body);
            setDocPossibleDocument({
                description: parsedBody.description,
                bullets: parsedBody.documents.map((document: any) => (document.documentName))
            });
        } catch (error: any) {
            console.log(error);
            setDocPossibleDocument({description: 'Unable to parse JSON: ' + error.message, bullets: []});
        }
        presentDocDetail();
    }

    return (
        <IonPage>

            <AcaHeader pageTitle="ACA Opportunity Details"></AcaHeader>
            <IonLoading isOpen={isLoading} message={loadingMessage}/>
            <IonContent fullscreen className="aca-detail">
                <div style={{padding: '10px'}}>
                    {opportunity &&
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Opportunity Details</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                {contact && contact.status === 'NO_CONTACT' && <div style={{fontWeight: 'bold', padding: '10px', backgroundColor: '#ffdddd', color: '#990000'}}>DO NOT CONTACT!!! Customer has requested to not be contacted.</div>}
                                <table style={{borderCollapse: 'separate', borderSpacing: '5px'}}>
                                    <tbody>
                                    <tr>
                                        <th>Opportunity ID:</th>
                                        <td>{opportunity.opportunityId}</td>
                                        <td style={{textAlign: 'right'}}></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <th>Status:</th>
                                        <td>{formatStatus(opportunity.status)}</td>
                                        <td style={{textAlign: 'right'}}></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <th>Stage:</th>
                                        <td>{opportunity.stageName}</td>
                                        <td>
                                            {opportunity.status === 'OPEN' && (tier1 || tier2 || manager) && availableStages &&
                                                <div>
                                                    <div style={{
                                                        width: '200px',
                                                        display: 'inline-block',
                                                    }}>
                                                        <IonSelect fill="outline" label="Change Stage" labelPlacement="floating" value={newStage} onIonChange={(e) => updateStage(e.detail.value)}>
                                                            {availableStages.map((item, index) => {
                                                                if (item.id === 6 && (tier2 && !manager)) {
                                                                    return (
                                                                        <IonSelectOption value={item.id} key={'selectOptStage3_' + item.id}>{item.name}</IonSelectOption>
                                                                    )
                                                                } else if (item.id === 6) {
                                                                } else {
                                                                    return (
                                                                        <IonSelectOption value={item.id} key={'selectOptStage3_' + item.id}>{item.name}</IonSelectOption>
                                                                    )
                                                                }
                                                            })
                                                            }
                                                        </IonSelect>
                                                    </div>
                                                </div>
                                            }
                                            {opportunity.status !== 'OPEN' && manager && availableStages && availableStages.length > 0 &&
                                                <div>
                                                    <div>
                                                        <IonSelect fill="outline" label="Change Stage" labelPlacement="floating" value={newStage} onIonChange={(e) => updateStage(e.detail.value)}>
                                                            {availableStages.map((item, index) => {
                                                                if (item.id === 6 && (tier2 && !manager)) {
                                                                    return (
                                                                        <IonSelectOption value={item.id} key={'selectOptStage2_' + item.id}>{item.name}</IonSelectOption>
                                                                    )
                                                                } else if (item.failed || item.id === 6) { // hide 6 First contact for !tier2, also always hide failed
                                                                } else {
                                                                    return (
                                                                        <IonSelectOption value={item.id} key={'selectOptStage2_' + item.id}>{item.name}</IonSelectOption>
                                                                    )
                                                                }
                                                            })
                                                            }
                                                        </IonSelect>
                                                    </div>
                                                </div>
                                            }
                                        </td>
                                        <td rowSpan={7} style={{verticalAlign: 'top'}}>
                                            {opportunity.verificationDate && opportunity.verificationDataRequested && opportunity.verificationDataRequested.length > 0 &&
                                                <>Verification Requested By:&nbsp;
                                                    {moment(opportunity.verificationDate).isBefore(moment()) &&
                                                        <span className="message sm danger">{formatDate(opportunity.verificationDate)}</span>}
                                                    {moment(opportunity.verificationDate).isBefore(moment().add(10, 'days')) && !(moment(opportunity.verificationDate).isBefore(moment())) &&
                                                        <span className="message sm warning">{formatDate(opportunity.verificationDate)}</span>}
                                                    {moment(opportunity.verificationDate).isAfter(moment().add(10, 'days')) && !(moment(opportunity.verificationDate).isBefore(moment().add(10, 'days'))) && <>{formatDate(opportunity.verificationDate)}</>}
                                                    <br/>
                                                    Documentation Requested:
                                                    {opportunity.verificationDataRequested &&
                                                        <ul style={{margin:'0px'}}>
                                                            {opportunity.verificationDataRequested?.map((item, index) => {
                                                                return (
                                                                    <li key={"opp_doc_" + item.id} style={{margin: '0px', padding: '0px'}}>
                                                                        {item.documentName} <IonButton className="xs-button" size="small" fill="clear" style={{verticalAlign:'top'}} onClick={()=> popupDocDocumentType(item.documentName || '', item.possibleDocuments || '')}><IonIcon md={informationOutline} ios={informationSharp}></IonIcon></IonButton>
                                                                    </li>);
                                                            })}
                                                        </ul>}
                                                </>
                                            }
                                        </td>
                                    </tr>
                                    {opportunity.failedReasonCode && <tr>
                                        <th style={{color: '#660000'}}>Failed Reason Code:</th>
                                        <td style={{color: '#660000'}}>{opportunity.failedReasonCode}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>}

                                    {opportunity.carrierProvider &&
                                        <tr>
                                            <th style={{color: '#006600'}}>Carrier Provider:</th>
                                            <td style={{color: '#006600'}}>{opportunity.carrierProvider}</td>
                                            <td style={{textAlign: 'right'}}></td>
                                            <td></td>
                                        </tr>
                                    }
                                    {opportunity.carrierState &&
                                        <tr>
                                            <th style={{color: '#006600'}}>Carrier State:</th>
                                            <td style={{color: '#006600'}}>{opportunity.carrierState}</td>
                                            <td style={{textAlign: 'right'}}></td>
                                            <td></td>
                                        </tr>
                                    }
                                    {opportunity.carrierMemberCount &&
                                        <tr>
                                            <th style={{color: '#006600'}}>Carrier Member Count:</th>
                                            <td style={{color: '#006600'}}>{opportunity.carrierMemberCount}</td>
                                            <td style={{textAlign: 'right'}}></td>
                                            <td></td>
                                        </tr>
                                    }

                                    <tr>
                                        <th>Assigned To:</th>
                                        <td>{opportunity.assignedUserName} {
                                            opportunity.assignedUserName && manager &&
                                            <IonButton fill="clear" size="small" onClick={() => removeAssignedUser()}><IonIcon md={trashOutline} ios={trashSharp}></IonIcon></IonButton>
                                        }</td>
                                        <td>{!opportunity.assignedUserName && tier2 && !manager && opportunity.stageId !== 1 && opportunity.status === 'OPEN' &&
                                            <IonButton size="small" style={{
                                                marginLeft: '10px',
                                                verticalAlign: 'middle'
                                            }} onClick={() => updateAssignedTo()}>Assign To Me</IonButton>
                                        }
                                        </td>
                                        <td></td>
                                    </tr>


                                    <tr>
                                        <th>Created Datetime:</th>
                                        <td>{formatDatetime(opportunity.createdDatetime)}</td>
                                        <td style={{textAlign: 'right'}}></td>
                                        <td></td>
                                    </tr>

                                    <tr>
                                        <th>Last Updated Datetime:</th>
                                        <td>{formatDatetime(opportunity.lastUpdatedDatetime)}</td>
                                        <td style={{textAlign: 'right'}}></td>
                                        <td></td>
                                    </tr>

                                    <tr>
                                        <th>Lead Gen Consent:</th>
                                        <td>
                                            {opportunity.leadSignedPdfId &&
                                                <>
                                                    <IonButton onClick={() => loadPdf(opportunity.leadSignedPdfId)} fill="outline" title="View PDF">
                                                        <IonIcon md={attachOutline} ios={attachSharp}></IonIcon> Signed
                                                        PDF
                                                    </IonButton>
                                                    {(tier1 || tier2 || manager) && opportunity.status === 'OPEN' &&
                                                        <>
                                                            <IonButton onClick={() => setChangingLeadPdf(true)} fill="clear" size="small" title="Manually update the document"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Change</IonButton>
                                                            <IonButton onClick={() => promptPdfReplace('LEAD_PDF')} fill="clear" size="small" title="Request document from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                        </>
                                                    }

                                                </>
                                            }
                                            {!opportunity.leadSignedPdfId && (tier1 || tier2 || manager) && opportunity.status === 'OPEN' &&
                                                <>
                                                    <IonButton onClick={() => setChangingLeadPdf(true)} fill="clear" size="small" title="Manually update the document"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Add
                                                        PDF</IonButton>
                                                    <IonButton onClick={() => promptPdfReplace('LEAD_PDF')} fill="clear" size="small" title="Request document from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                </>
                                            }
                                            {changingLeadPdf &&
                                                <>

                                                    <IonItem key="upload"><AcaUploadFile callback={addLeadAttachment}/></IonItem>
                                                </>
                                            }
                                        </td>
                                        <td style={{textAlign: 'right'}}></td>
                                        <td></td>
                                    </tr>

                                    <tr>
                                        <th>Agent Of Record Consent:</th>
                                        <td>
                                            {opportunity.aorSignedPdfId && (tier2 || manager) &&
                                                <>
                                                    <IonButton onClick={() => loadPdf(opportunity.aorSignedPdfId)} fill="outline" title="View PDF">
                                                        <IonIcon md={attachOutline} ios={attachSharp}></IonIcon> Signed
                                                        PDF
                                                    </IonButton>
                                                    {(tier1 || tier2 || manager) && opportunity.status === 'OPEN' &&
                                                        <>
                                                            <IonButton onClick={() => setChangingAorPdf(true)} fill="clear" size="small" title="Manually update the document"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Change</IonButton>
                                                            <IonButton onClick={() => promptPdfReplace('AOR_PDF')} fill="clear" size="small" title="Request document from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                        </>
                                                    }
                                                </>
                                            }
                                            {opportunity.aorSignedPdfId && (!tier2 && !manager) &&
                                                <>Exists, but only available to Tier2 or Managers
                                                    {(tier1 || tier2 || manager) && opportunity.status === 'OPEN' &&
                                                        <>
                                                            <IonButton onClick={() => setChangingAorPdf(true)} fill="clear" size="small" title="Manually update the document"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Change</IonButton>
                                                            <IonButton onClick={() => promptPdfReplace('AOR_PDF')} fill="clear" size="small" title="Request document from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                        </>
                                                    }
                                                </>
                                            }
                                            {!opportunity.aorSignedPdfId && opportunity.status === 'OPEN' &&
                                                <>
                                                    <IonButton onClick={() => setChangingAorPdf(true)} fill="clear" size="small"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Add
                                                        PDF</IonButton>
                                                    <IonButton onClick={() => promptPdfReplace('AOR_PDF')} fill="clear" size="small" title="Request document from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                </>
                                            }
                                            {changingAorPdf &&
                                                <>

                                                    <IonItem key="upload"><AcaUploadFile callback={addAorAttachment}/></IonItem>
                                                </>
                                            }
                                        </td>
                                        <td style={{textAlign: 'right'}}></td>
                                        <td></td>
                                    </tr>




                                    <tr>
                                        <th>Created By:</th>
                                        <td>{opportunity.createdByUserName} {opportunity.createdByUserIdRef && <>({opportunity.createdByUserIdRef})</>}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>


                                    <tr>
                                        <th>Source:</th>
                                        <td>{opportunity.source}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>


                                    <tr>
                                        <th>Agent Fusion ID:</th>
                                        <td>{opportunity.agentFusionId}</td>
                                    </tr>


                                    <tr>
                                        <th>Custom Field #1:</th>
                                        <td>{opportunity.customField1}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>


                                    <tr>
                                        <th>Custom Field #2:</th>
                                        <td>{opportunity.customField2}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>

                                    </tbody>
                                </table>

                                <p style={{textAlign: 'right', marginTop: '40px'}}>
                                    {opportunity.status === 'OPEN' && (tier1 || tier2 || manager) &&
                                        <IonButton fill="outline" onClick={() => handleCloseOpp()}>Close
                                            Opportunity</IonButton>
                                    }
                                    {opportunity.status === 'OPEN' && !(tier1 || tier2 || manager) &&
                                        <IonButton fill="outline" disabled={true}>Close Opportunity</IonButton>
                                    }
                                </p>
                            </IonCardContent>
                        </IonCard>
                    }

                    {contact &&
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Contact Details
                                    &nbsp;
                                    <IonButton size="small" fill="clear" style={{verticalAlign: 'middle'}} routerLink={"/aca/view-contact/" + contact.contactId} routerDirection="none" title="View Contact"><IonIcon md={personOutline} ios={personSharp}></IonIcon></IonButton>
                                </IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                {!masked && <div style={{fontWeight: 'bold', padding: '10px', backgroundColor: '#ffdddd', color: '#990000'}}>Warning!!! Data is not masked!</div>}
                                <div style={{float: 'left'}}>
                                    <table>
                                        <tbody>
                                        <tr>
                                            <th>Contact ID:</th>
                                            <td style={{verticalAlign: "middle"}}>
                                                {contact.contactId}
                                                &nbsp;&nbsp;
                                                {contact && contact.contactId &&
                                                    <>
                                                        <IonButton size="small" fill="clear" style={{verticalAlign: "middle"}} onClick={() => copyToClipboard(contact.contactId)} title="Copy Contact ID">
                                                            <IonIcon id="copyContactBtn" md={copyOutline} ios={copySharp}></IonIcon>
                                                        </IonButton>
                                                        <IonPopover trigger="copyContactBtn" triggerAction="click"
                                                            isOpen={showPopover}
                                                            onDidDismiss={() => setShowPopover(false)}
                                                                    onIonPopoverDidPresent={() => setTimeout(setShowPopover, 1000, false)}
                                                        ><IonContent className="ion-padding">Copied to clipboard</IonContent></IonPopover>
                                                    </>
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>First Name:</th>
                                            <td>{contact.firstName}</td>
                                        </tr>
                                        <tr>
                                            <th>Middle Name:</th>
                                            <td>{contact.middleName}</td>
                                        </tr>
                                        <tr>
                                            <th>Last Name:</th>
                                            <td>{contact.lastName}</td>
                                        </tr>
                                        <tr>
                                            <th>Suffix:</th>
                                            <td>{contact.suffix}</td>
                                        </tr>
                                        <tr>
                                            <th>Email:</th>
                                            <td>{contact.email}</td>
                                        </tr>
                                        <tr>
                                            <th>Phone:</th>
                                            <td>{formatPhone(contact.phone)}</td>
                                        </tr>
                                        <tr>
                                            <th>Address:</th>
                                            <td>
                                                {contact.addressLine1}
                                                {!contact.addressLine1 && <MissingItem field="Address Line 1" />}
                                                <br/>
                                                {contact.addressLine2 &&
                                                    <>{contact.addressLine2}<br/></>
                                                }
                                                {contact.addressCity}
                                                {!contact.addressCity && <MissingItem field="Address Zip Code" />}
                                                , {contact.addressState} {contact.addressZip}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Date Of Birth:</th>
                                            <td>
                                                <>{contact.dob}</>
                                                {!contact.dob && <MissingItem field="Date of Birth" />}
                                                {masked && (tier2 || manager) && <>&nbsp;&nbsp;<a onClick={() => unmaskData()}><IonIcon md={eyeOutline} ios={eyeSharp}></IonIcon></a></>}
                                                {!masked && (tier2 || manager) && <>&nbsp;&nbsp;<a onClick={() => maskData()}><IonIcon md={eyeOffOutline} ios={eyeOffSharp}></IonIcon></a></>}</td>
                                        </tr>
                                        <tr>
                                            <th>SSN:</th>
                                            <td>{contact.ssn}
                                                {!contact.ssn && <MissingItem field="Social Security Number" />}
                                                &nbsp;&nbsp;
                                                {masked && (tier2 || manager) && <a onClick={() => unmaskData()}><IonIcon md={eyeOutline} ios={eyeSharp}></IonIcon></a>}
                                                {!masked && (tier2 || manager) && <a onClick={() => maskData()}><IonIcon md={eyeOffOutline} ios={eyeOffSharp}></IonIcon></a>}</td>
                                        </tr>
                                        </tbody>
                                    </table>

                                </div>


                                <table style={{float: 'left'}}>
                                    <tbody>

                                    <tr>
                                        <th>Contact Status:</th>
                                        <td>{formatStatus(contact.status)}</td>
                                    </tr>
                                    <tr>
                                        <th>Created Datetime:</th>
                                        <td>{formatDatetime(contact.createdDatetime)}</td>
                                    </tr>

                                    <tr>
                                        <th>Last Updated Datetime:</th>
                                        <td>{formatDatetime(contact.lastUpdatedDatetime)}</td>
                                    </tr>
                                    <tr>
                                        <th>Fusion Customer ID:</th>
                                        <td>{contact.fusionCustomerId}</td>
                                    </tr>
                                    <tr>
                                        <th>ETC:</th>
                                        <td>{contact.etc}</td>
                                    </tr>
                                    <tr>
                                        <th>Annual Income:</th>
                                        <td>
                                            {contact.annualIncome}
                                            {!contact.annualIncome && contact.annualIncome !== 0 && <MissingItem field="Annual Income" />}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Employment Type:</th>
                                        <td>
                                            {contact.employmentType}
                                            {!contact.employmentType && <MissingItem field="Employment Type" />}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Employment Description:</th>
                                        <td>{contact.employmentDescription}</td>
                                    </tr>
                                    <tr>
                                        <th>Main Doctor:</th>
                                        <td>{contact.mainDoctor}</td>
                                    </tr>
                                    <tr>
                                        <th>Medications:</th>
                                        <td>{contact.medications}</td>
                                    </tr>
                                    <tr>
                                        <th>Smoker:</th>
                                        <td>{contact.smoker ? 'Yes' : 'No'}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>
                                            <div style={{display: 'inline-block'}}>
                                                {contact.photo1Id &&
                                                    <IonButton onClick={() => expandImage(contact.photo1Id)} fill="outline">
                                                        <IonIcon md={attachOutline} ios={attachSharp}></IonIcon> Photo 1
                                                    </IonButton>
                                                }
                                                {contact.photo2Id &&
                                                    <IonButton onClick={() => expandImage(contact.photo2Id)} fill="outline">
                                                        <IonIcon md={attachOutline} ios={attachSharp}></IonIcon> Photo 2
                                                    </IonButton>
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                            </IonCardContent>
                        </IonCard>
                    }
                    {contact &&
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Notes</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <div>
                                    {showNotes && <>
                                        <IonButton onClick={() => toggleNotes(true)}>Notes</IonButton>
                                        <IonButton fill="outline" onClick={() => toggleNotes(false)}>History</IonButton>
                                        <IonButton fill="clear" style={{float: "right"}} onClick={() => popupAddNote()}><IonIcon md={addOutline} ios={addSharp}></IonIcon> Add
                                            Note</IonButton>
                                        <div style={{
                                            paddingRight: '20px',
                                            overflow: 'auto',
                                            height: '400px',
                                            border: '1px solid grey'
                                        }}>
                                            {contactNotes && contactNotes.map((note, index) => {
                                                return (
                                                    <div style={{
                                                        position: 'relative',
                                                        padding: '10px',
                                                        borderTop: '1px solid #cccccc'
                                                    }} key={'note_' + note.noteId}>
                                                        <p>{note.noteText}</p>
                                                        <div className="note" style={{
                                                            display: 'inline-block',
                                                            position: "absolute",
                                                            bottom: '0px',
                                                            right: '0px',
                                                        }}>
                                                            {note.userName} - {formatDatetime(note.createdDatetime)}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                            }
                                            {(!contactNotes || contactNotes.length === 0) &&
                                                <div style={{
                                                    position: 'relative',
                                                    color: '#cccccc',
                                                    height: '100%',
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle'
                                                }}>
                                                    <div style={{
                                                        display: 'inline-block',
                                                        verticalAlign: 'middle',
                                                        top: '50%',
                                                        position: 'relative'
                                                    }}>
                                                        No Notes
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </>}
                                    {!showNotes && <>
                                        <IonButton fill="outline" onClick={() => toggleNotes(true)}>Notes</IonButton>
                                        <IonButton onClick={() => toggleNotes(false)}>History</IonButton>
                                        <IonButton fill="clear" style={{float: "right"}} onClick={() => popupAddNote()}><IonIcon md={addOutline} ios={addSharp}></IonIcon> Add
                                            Note</IonButton>

                                        <div style={{
                                            paddingRight: '20px',
                                            overflow: 'auto',
                                            height: '400px',
                                            border: '1px solid grey'
                                        }}>

                                            {historyEntries && historyEntries.map((entry, index) => {
                                                return (
                                                    <div style={{
                                                        position: 'relative',
                                                        padding: '10px',
                                                        borderTop: '1px solid #cccccc'
                                                    }} key={'history_' + index}>
                                                        <div dangerouslySetInnerHTML={{
                                                            __html: entry.changeText
                                                        }}></div>
                                                        <div className="note" style={{
                                                            display: 'inline-block',
                                                            position: "absolute",
                                                            bottom: '0px',
                                                            right: '0px',
                                                        }}>
                                                            {entry.userName} - {formatDatetime(entry.createdDatetime)}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                            {(!historyEntries || historyEntries.length === 0) &&
                                                <div style={{
                                                    position: 'relative',
                                                    color: '#cccccc',
                                                    height: '100%',
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle'
                                                }}>
                                                    <div style={{
                                                        display: 'inline-block',
                                                        verticalAlign: 'middle',
                                                        top: '50%',
                                                        position: 'relative'
                                                    }}>
                                                        No History
                                                    </div>
                                                </div>
                                            }


                                        </div>
                                    </>}
                                </div>
                            </IonCardContent>
                        </IonCard>
                    }
                </div>
            </IonContent>
        </IonPage>
    )
}

export default AcaViewOpportunity;